// src/hooks/useAirbnbBookings.js
import { useState, useCallback } from 'react';
import { useAirbnbAuth } from '../context/AirbnbAuthContext.js';
import { db } from '../firebase.js';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs, Timestamp } from 'firebase/firestore';

export function useAirbnbBookings() {
  const { token } = useAirbnbAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch all bookings
  const fetchBookings = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/airbnb/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setBookings(data.bookings);
      } else {
        setError(data.error || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL]);

  // Create new booking
  const createBooking = useCallback(async (bookingDate, clientInfo) => {
    if (!token) {
      setError('Not authenticated');
      return { success: false, error: 'Not authenticated' };
    }

    setLoading(true);
    setError(null);

    try {
      // First create in PostgreSQL via API
      const response = await fetch(`${API_URL}/api/airbnb/bookings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookingDate })
      });

      const data = await response.json();

      if (response.ok) {
        // Now create in Firebase Calendar (Team 2)
        try {
          // 1. Search for existing client by phone or email
          let matchingClientId = null;

          // Try to find by phone first
          if (clientInfo.phone) {
            const phoneQuery = query(
              collection(db, 'clients'),
              where('phone', '==', clientInfo.phone)
            );
            const phoneSnapshot = await getDocs(phoneQuery);
            if (!phoneSnapshot.empty) {
              matchingClientId = phoneSnapshot.docs[0].id;
              console.log('✅ Found existing client by phone:', matchingClientId);
            }
          }

          // If not found by phone, try email
          if (!matchingClientId && clientInfo.email) {
            const emailQuery = query(
              collection(db, 'clients'),
              where('email', '==', clientInfo.email)
            );
            const emailSnapshot = await getDocs(emailQuery);
            if (!emailSnapshot.empty) {
              matchingClientId = emailSnapshot.docs[0].id;
              console.log('✅ Found existing client by email:', matchingClientId);
            }
          }

          // If no match found, create new client in Firestore
          if (!matchingClientId) {
            const newClient = {
              name: clientInfo.name || clientInfo.email,
              email: clientInfo.email,
              phone: clientInfo.phone,
              address: clientInfo.address,
              paymentMethod: 'cash',
              hours: 2,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              source: 'airbnb_booking'
            };
            const clientDocRef = await addDoc(collection(db, 'clients'), newClient);
            matchingClientId = clientDocRef.id;
            console.log('✅ Created new client in Firestore:', matchingClientId);
          }

          // 2. bookingDate is already in YYYY-MM-DD format (correct for parseISO)
          // Calculate end time (10:00 + 2 hours = 12:00)
          const startTime = '10:00';
          const endTime = '12:00';

          // 3. Create job with clientId
          const jobData = {
            clientId: matchingClientId,
            clientName: clientInfo.name || clientInfo.email,
            address: clientInfo.address,
            phone: clientInfo.phone,
            date: bookingDate, // Keep YYYY-MM-DD format for parseISO
            startTime: startTime,
            endTime: endTime,
            duration: 2,
            notes: 'Airbnb Booking',
            employeeIds: [],
            status: 'pending',
            invoiceAmount: 0,
            invoiceSent: false,
            checklist: [],
            photos: [],
            isTimeModified: false,
            recurrenceType: 'none',
            recurrenceId: null,
            team: 'team2',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            source: 'airbnb_booking',
            airbnb_booking_id: data.booking.id
          };

          console.log('📅 Creating calendar job:', jobData);
          const docRef = await addDoc(collection(db, 'jobs'), jobData);
          console.log('✅ Calendar job created:', docRef.id);

        } catch (calendarError) {
          console.error('❌ Calendar sync error:', calendarError);
          // Continue even if calendar fails
        }

        // Refresh bookings list
        await fetchBookings();
        return { success: true, booking: data.booking };
      } else {
        setError(data.error || 'Failed to create booking');
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to create booking';
      setError(errorMsg);
      console.error('Error creating booking:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, fetchBookings]);

  // Update booking date
  const updateBooking = useCallback(async (bookingId, newDate) => {
    console.log('🔄 updateBooking called:', { bookingId, newDate });

    if (!token) {
      setError('Not authenticated');
      return { success: false, error: 'Not authenticated' };
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Update in PostgreSQL
      console.log('📤 Sending PUT request to PostgreSQL...');
      const response = await fetch(`${API_URL}/api/airbnb/bookings/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bookingDate: newDate })
      });

      const data = await response.json();
      console.log('📥 PostgreSQL response:', data);

      if (response.ok) {
        // 2. Update job in Firestore
        console.log('🔍 Searching for job in Firestore with airbnb_booking_id:', bookingId);
        try {
          // Find the job by airbnb_booking_id
          const jobsQuery = query(
            collection(db, 'jobs'),
            where('airbnb_booking_id', '==', bookingId)
          );
          const jobsSnapshot = await getDocs(jobsQuery);
          console.log('📊 Found jobs:', jobsSnapshot.size);

          if (!jobsSnapshot.empty) {
            const jobDoc = jobsSnapshot.docs[0];
            console.log('📝 Updating job:', jobDoc.id, 'to date:', newDate);
            await updateDoc(doc(db, 'jobs', jobDoc.id), {
              date: newDate, // YYYY-MM-DD format
              updatedAt: Timestamp.now()
            });
            console.log('✅ Updated job in Firestore:', jobDoc.id);
          } else {
            console.warn('⚠️ No job found in Firestore for booking:', bookingId);
          }
        } catch (firestoreError) {
          console.error('❌ Error updating Firestore job:', firestoreError);
          // Continue even if Firestore update fails
        }

        // Refresh bookings list
        await fetchBookings();
        return { success: true, booking: data.booking };
      } else {
        setError(data.error || 'Failed to update booking');
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to update booking';
      setError(errorMsg);
      console.error('Error updating booking:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, fetchBookings]);

  // Cancel booking
  const cancelBooking = useCallback(async (bookingId) => {
    if (!token) {
      setError('Not authenticated');
      return { success: false, error: 'Not authenticated' };
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Delete from Firestore first
      try {
        const jobsQuery = query(
          collection(db, 'jobs'),
          where('airbnb_booking_id', '==', bookingId)
        );
        const jobsSnapshot = await getDocs(jobsQuery);

        if (!jobsSnapshot.empty) {
          const jobDoc = jobsSnapshot.docs[0];
          await deleteDoc(doc(db, 'jobs', jobDoc.id));
          console.log('✅ Deleted job from Firestore:', jobDoc.id);
        } else {
          console.warn('⚠️ No job found in Firestore for booking:', bookingId);
        }
      } catch (firestoreError) {
        console.error('❌ Error deleting Firestore job:', firestoreError);
        // Continue even if Firestore delete fails
      }

      // 2. Delete from PostgreSQL
      const response = await fetch(`${API_URL}/api/airbnb/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh bookings list
        await fetchBookings();
        return { success: true, message: data.message };
      } else {
        setError(data.error || 'Failed to cancel booking');
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to cancel booking';
      setError(errorMsg);
      console.error('Error cancelling booking:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, fetchBookings]);

  return {
    bookings,
    loading,
    error,
    fetchBookings,
    createBooking,
    updateBooking,
    cancelBooking
  };
}
